import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import DistributorClinicUserEditor from './DistributorClinicUserEditor';

import {
  DISTRIBUTOR_PASSWORD_PATTERN as passwordPattern,
  DISTRIBUTOR_PASSWORD_REQUIREMENT as passwordRequirement,
} from '../../utils/distributorPassword';
const emptyInvite = () => ({ firstName: '', lastName: '', email: '', password: '' });
const validUser = (row) =>
  typeof row?._id === 'string' &&
  row._id.length > 0 &&
  ['firstName', 'lastName', 'email'].every((key) => typeof row[key] === 'string') &&
  typeof row.isActive === 'boolean' &&
  typeof row.acceptancePending === 'boolean';

export default function DistributorClinicUsers({
  clinicId,
  compact = false,
  distributorId,
  onBusyChange,
  readOnly = false,
  t,
}) {
  const hintId = useId();
  const [inviteOpen, setInviteOpen] = useState(false);
  const actionTrigger = useRef(null);
  const editorRef = useRef(null);
  const restoreFocus = useRef(false);
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(emptyInvite);
  const [reset, setReset] = useState(null);
  const [edit, setEdit] = useState(null);
  const mounted = useRef(false);
  const viewSession = useRef(captureSession());
  const selected = useRef(clinicId);
  const request = useRef(0);
  const reading = useRef(true);
  const pending = useRef(null);
  selected.current = clinicId;

  const load = useCallback(async () => {
    const session = viewSession.current;
    if (!mounted.current || !isSessionCurrent(session)) return;
    const generation = ++request.current;
    const current = () =>
      mounted.current &&
      selected.current === clinicId &&
      request.current === generation &&
      isSessionCurrent(session);
    reading.current = true;
    setLoading(true);
    try {
      const response = await distributorService.listUsers(
        clinicId,
        ...(distributorId ? [distributorId] : [])
      );
      if (!current()) return;
      const rows = response?.data?.data;
      if (!Array.isArray(rows) || !rows.every(validUser)) throw new Error('Invalid account list');
      setUsers(rows);
      setLoadError('');
    } catch {
      if (current()) setLoadError('Could not load clinic users. Please try again.');
    } finally {
      if (mounted.current && selected.current === clinicId && request.current === generation) {
        reading.current = false;
        setLoading(false);
      }
    }
  }, [clinicId, distributorId]);
  useEffect(() => {
    mounted.current = true;
    viewSession.current = captureSession();
    pending.current = null;
    setBusy(false);
    setUsers(null);
    setError('');
    setLoadError('');
    setNotice('');
    setReset(null);
    setEdit(null);
    setInvite(emptyInvite());
    setInviteOpen(false);
    load();
    return () => {
      mounted.current = false;
      request.current += 1;
    };
  }, [load]);

  const dirty = Object.values(invite).some(Boolean) || reset !== null || edit !== null;
  useEffect(() => {
    onBusyChange?.(busy || dirty);
    const warn = (event) => {
      if (busy || dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      onBusyChange?.(false);
      window.removeEventListener('beforeunload', warn);
    };
  }, [busy, dirty, onBusyChange]);

  const begin = () => {
    if (
      readOnly ||
      pending.current ||
      reading.current ||
      !users ||
      loadError ||
      !isSessionCurrent(viewSession.current)
    )
      return null;
    const operation = { clinicId, session: captureSession() };
    pending.current = operation;
    setBusy(true);
    setError('');
    setNotice('');
    return operation;
  };
  const current = (operation) =>
    mounted.current &&
    pending.current === operation &&
    selected.current === operation.clinicId &&
    isSessionCurrent(operation.session);
  const finish = (operation) => {
    if (pending.current !== operation) return;
    pending.current = null;
    if (mounted.current && selected.current === operation.clinicId) setBusy(false);
  };
  const create = async (event) => {
    event.preventDefault();
    const operation = begin();
    if (!operation) return;
    const submitted = { ...invite };
    if (!passwordPattern.test(submitted.password)) {
      setError(passwordRequirement);
      finish(operation);
      return;
    }
    try {
      const response = await distributorService.createUser(
        clinicId,
        submitted,
        ...(distributorId ? [distributorId] : [])
      );
      if (!current(operation)) return;
      const receipt = response?.data?.data;
      if (
        !validUser(receipt) ||
        receipt.isActive !== true ||
        receipt.acceptancePending !== true ||
        receipt.firstName !== submitted.firstName.trim() ||
        receipt.lastName !== submitted.lastName.trim() ||
        receipt.email !== submitted.email.toLowerCase().trim()
      )
        throw new Error('Invalid account receipt');
      setInvite(emptyInvite());
      setInviteOpen(false);
      restoreFocus.current = true;
      setNotice(
        'Clinic login created. The user must accept the Terms and Privacy Policy after signing in.'
      );
    } catch {
      if (!current(operation)) return;
      setError('Account creation could not be confirmed. Refresh the list before trying again.');
    } finally {
      if (current(operation)) await load();
      finish(operation);
    }
  };
  const status = async (row) => {
    const operation = begin();
    if (!operation) return;
    try {
      const response = await distributorService.setUserStatus(
        clinicId,
        row._id,
        {
          isActive: !row.isActive,
          expectedIsActive: row.isActive,
        },
        ...(distributorId ? [distributorId] : [])
      );
      if (!current(operation)) return;
      const receipt = response?.data?.data;
      if (receipt?._id !== row._id || receipt.isActive !== !row.isActive)
        throw new Error('Invalid status receipt');
      setNotice('Account status updated.');
    } catch {
      if (!current(operation)) return;
      setError(
        'Account status could not be confirmed. Reload and review the current list before trying again.'
      );
    } finally {
      if (current(operation)) await load();
      finish(operation);
    }
  };
  const saveProfile = async (row, changes) => {
    const operation = begin();
    if (!operation) return;
    try {
      const receipt = (
        await distributorService.saveAdminCustomerUser(distributorId, clinicId, row._id, {
          expectedRevision: row.revision,
          changes,
        })
      )?.data?.data;
      if (!current(operation)) return;
      if (
        !validUser(receipt) ||
        receipt._id !== row._id ||
        !/^[a-f\d]{64}$/.test(receipt.revision) ||
        !Object.entries(changes).every(
          ([key, value]) =>
            receipt[key] === (key === 'email' ? value.trim().toLowerCase() : value.trim())
        )
      )
        throw new Error('Unconfirmed account save');
      setEdit(null);
      setNotice('Account saved. All prior sessions were revoked.');
    } catch {
      if (current(operation))
        setError(
          'Account changes could not be confirmed. Reload the account before trying again. Your edits are still here.'
        );
    } finally {
      if (current(operation)) await load();
      finish(operation);
    }
  };
  const resetPassword = async (event) => {
    event.preventDefault();
    const row = users?.find((item) => item._id === reset?.id);
    if (!row) return;
    const operation = begin();
    if (!operation) return;
    if (!passwordPattern.test(reset.password)) {
      setError(passwordRequirement);
      finish(operation);
      return;
    }
    try {
      const response = await distributorService.resetUserPassword(
        clinicId,
        row._id,
        {
          password: reset.password,
          expectedIsActive: row.isActive,
        },
        ...(distributorId ? [distributorId] : [])
      );
      if (!current(operation)) return;
      const receipt = response?.data?.data;
      if (receipt?._id !== row._id || receipt.passwordResetApplied !== true)
        throw new Error('Invalid reset receipt');
      setReset(null);
      restoreFocus.current = true;
      setNotice('Password reset. All prior sessions were revoked.');
    } catch {
      if (!current(operation)) return;
      setError('Password reset could not be confirmed. Review the account before trying again.');
    } finally {
      if (current(operation)) await load();
      finish(operation);
    }
  };
  const disabled = busy || loading || !users || Boolean(loadError);
  const hasAction = Boolean(
    reset || edit || (compact ? inviteOpen : Object.values(invite).some(Boolean))
  );
  const resetId = reset?.id;
  useEffect(() => {
    if (compact && (inviteOpen || resetId)) editorRef.current?.querySelector('input')?.focus();
    // Focus only when opening an editor, never while typing into it.
  }, [compact, inviteOpen, resetId]);
  useEffect(() => {
    if (restoreFocus.current && !busy && !loading) {
      restoreFocus.current = false;
      const trigger = actionTrigger.current;
      if (!trigger?.closest('[hidden], [inert]')) trigger?.focus();
    }
  }, [busy, loading, inviteOpen, reset]);
  const cancelInvite = () => {
    setInvite(emptyInvite());
    setInviteOpen(false);
    restoreFocus.current = true;
  };
  const cancelReset = () => {
    setReset(null);
    restoreFocus.current = true;
  };
  return (
    <section
      aria-label={t('Clinic users')}
      className={`me-dw-users space-y-4 ${compact ? 'me-dw-customer-logins' : 'border-t border-[#e6eeee] pt-5'}`}
    >
      <div className="me-dw-customer-login-heading">
        <h3 className="text-base font-bold text-[#153d48]">{t('Clinic users')}</h3>
        {compact && !readOnly && (
          <button
            type="button"
            className="me-dw-primary"
            disabled={disabled || hasAction}
            onClick={(event) => {
              actionTrigger.current = event.currentTarget;
              setInviteOpen(true);
            }}
          >
            <span aria-hidden="true">+ </span>
            {t('New login')}
          </button>
        )}
      </div>
      {!compact && !readOnly && (
        <p className="text-sm text-slate-600">
          {t(
            'Create a clinic login with a temporary password. Share it privately with the user. They must accept the Terms and Privacy Policy themselves.'
          )}
        </p>
      )}
      {(error || loadError) && (
        <p role="alert" className="text-red-700">
          {error && t(error)} {loadError && t(loadError)}{' '}
          <button
            type="button"
            className="underline"
            disabled={busy || loading}
            onClick={() => load()}
          >
            {t('Retry')}
          </button>
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {t(notice)}
        </p>
      )}
      {loading && !users && <p role="status">{t('Loading clinic users...')}</p>}
      {users?.length === 0 && (
        <p className="me-dw-subpanel p-4 text-sm text-slate-600">{t('No clinic users yet.')}</p>
      )}
      {users?.length > 0 && (
        <ul className="me-dw-list overflow-hidden rounded-xl border border-[#e1ebeb]">
          {users.map((row) => (
            <li
              key={row._id}
              className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm"
            >
              <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                <strong className="block">
                  {row.firstName} {row.lastName}
                </strong>
                <span className="block text-slate-600">{row.email}</span>
                <span className="mt-1 inline-block text-xs font-semibold text-[#0b7378]">
                  {!row.isActive
                    ? t('Inactive')
                    : row.acceptancePending
                      ? t('Pending acceptance')
                      : t('Active')}
                </span>
              </span>
              {!readOnly && (
                <span className="flex flex-wrap gap-3">
                  {distributorId && (
                    <button
                      type="button"
                      disabled={disabled || hasAction || !row.revision}
                      className="me-dw-link"
                      onClick={() => setEdit(row)}
                    >
                      {t('Edit login')}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={disabled || hasAction}
                    className="me-dw-link"
                    onClick={(event) => {
                      actionTrigger.current = event.currentTarget;
                      setReset({ id: row._id, password: '' });
                    }}
                  >
                    {t('Reset password')}
                  </button>
                  <button
                    type="button"
                    disabled={disabled || hasAction}
                    className={`me-dw-link ${compact && row.isActive ? 'me-dw-customer-deactivate' : ''}`}
                    onClick={() => status(row)}
                  >
                    {t(row.isActive ? 'Deactivate' : 'Reactivate')}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {!readOnly && edit && (
        <DistributorClinicUserEditor
          key={edit._id}
          row={edit}
          disabled={disabled}
          onSave={saveProfile}
          onCancel={() => setEdit(null)}
          t={t}
        />
      )}
      {!readOnly && reset && (
        <form
          ref={editorRef}
          onSubmit={resetPassword}
          className="me-dw-subpanel flex flex-wrap items-end gap-3 p-4"
        >
          {compact && (
            <h4 className="basis-full font-semibold">
              {t('Reset password')}: {users?.find((row) => row._id === reset.id)?.email}
            </h4>
          )}
          <label>
            {t('New temporary password')}
            <input
              type="password"
              autoComplete="new-password"
              disabled={disabled}
              minLength={12}
              maxLength={72}
              pattern={passwordPattern.source}
              title={t(passwordRequirement)}
              aria-describedby={`${hintId}-reset`}
              required
              className="me-dw-field block rounded-lg border px-3 py-2"
              value={reset.password}
              onChange={(event) =>
                setReset((current) => ({ ...current, password: event.target.value }))
              }
            />
          </label>
          <p id={`${hintId}-reset`} className="basis-full text-xs text-slate-600">
            {t(passwordRequirement)}
          </p>
          <button type="submit" disabled={disabled} className="me-dw-primary">
            {t('Reset password')}
          </button>
          <button
            type="button"
            disabled={busy || loading}
            onClick={cancelReset}
            className="me-dw-link text-sm"
          >
            {t('Cancel')}
          </button>
        </form>
      )}
      {!readOnly && (!compact || inviteOpen) && (
        <form ref={editorRef} onSubmit={create} className="me-dw-subpanel space-y-4 p-4 sm:p-5">
          <h4 className="font-bold text-[#153d48]">{t('Create clinic login')}</h4>
          {compact && (
            <p className="text-sm text-slate-600">
              {t(
                'Create a clinic login with a temporary password. Share it privately with the user. They must accept the Terms and Privacy Policy themselves.'
              )}
            </p>
          )}
          <fieldset
            disabled={disabled || !!reset || !!edit}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {['firstName', 'lastName', 'email'].map((key) => (
              <label key={key}>
                {t({ firstName: 'First name', lastName: 'Last name', email: 'Email' }[key])}
                <input
                  className="me-dw-field block w-full rounded-lg border px-3 py-2"
                  type={key === 'email' ? 'email' : 'text'}
                  required
                  maxLength={key === 'email' ? 254 : 100}
                  value={invite[key]}
                  onChange={(event) =>
                    setInvite((current) => ({ ...current, [key]: event.target.value }))
                  }
                />
              </label>
            ))}
            <label>
              {t('Temporary password')}
              <input
                className="me-dw-field block w-full rounded-lg border px-3 py-2"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                maxLength={72}
                pattern={passwordPattern.source}
                title={t(passwordRequirement)}
                aria-describedby={`${hintId}-create`}
                value={invite.password}
                onChange={(event) =>
                  setInvite((current) => ({ ...current, password: event.target.value }))
                }
              />
            </label>
          </fieldset>
          <p id={`${hintId}-create`} className="text-xs text-slate-600">
            {t(passwordRequirement)}
          </p>
          <button type="submit" disabled={disabled || !!reset || !!edit} className="me-dw-primary">
            {t('Create login')}
          </button>
          {(compact || Object.values(invite).some(Boolean)) && (
            <button
              type="button"
              disabled={busy}
              onClick={cancelInvite}
              className="me-dw-link ml-3"
            >
              {t(compact ? 'Cancel' : 'Discard changes')}
            </button>
          )}
        </form>
      )}
    </section>
  );
}
